import { useEffect, useRef, useState } from 'react';
import { getProductBySlug, quoteMerchandise, type MerchandiseQuote, type StorefrontProduct } from '../api/client';
import { orderableVariant, readPendingLines, type PendingLine } from './pendingLine';
import { useIsoLayoutEffect } from '../elements/render/useIsoLayoutEffect';

// What the cart holds and what the server says it costs, kept in step with every change to its lines. The
// products behind the lines are kept between changes, so a stepper press re-prices the cart without reading
// every product again or blanking the lines while it does; a retry reads them all afresh.

/** The cart's lines, the products behind them, the server's price for them, and how to change them. */
export type CartLines = {
  lines: PendingLine[];
  products: StorefrontProduct[];
  quote: MerchandiseQuote | null;
  loading: boolean;
  error: string;
  /** Replaces the lines; products the caller already holds are kept, so they need not be read again. */
  setLines: (next: PendingLine[], known?: StorefrontProduct[]) => void;
  fail: (message: string) => void;
  retry: () => void;
};

const UNAVAILABLE = 'An item is no longer available in the quantity chosen. Lower it or remove it to continue.';
const LOAD_FAILED = 'Your cart could not be loaded. Please try again.';

// Keeps the lines for this visit only, as identifiers and quantities.
function persist(key: string, lines: PendingLine[]): void {
  try {
    if (lines.length) sessionStorage.setItem(key, JSON.stringify(lines));
    else sessionStorage.removeItem(key);
  } catch { /* Storage may be disabled; the cart then lasts as long as the page. */ }
}

// Reads the lines this visit stored, or none where storage is unavailable.
function restore(key: string): PendingLine[] {
  try { return readPendingLines(sessionStorage.getItem(key)); } catch { return []; }
}

// Adds the products a caller already holds to the ones kept, the newer copy winning.
const withKnown = (held: StorefrontProduct[], known: StorefrontProduct[]): StorefrontProduct[] =>
  [...held.filter((row) => !known.some((fresh) => fresh.slug === row.slug)), ...known];

// Reads the products the lines name that are not already held, in the order the lines name them.
async function loadProducts(lines: PendingLine[], held: StorefrontProduct[]): Promise<StorefrontProduct[]> {
  const slugs = [...new Set(lines.map((row) => row.slug))];
  const fetched = await Promise.all(slugs.filter((slug) => !held.some((row) => row.slug === slug)).map(getProductBySlug));
  const all = [...held, ...fetched];
  return slugs.map((slug) => all.find((row) => row.slug === slug)).filter((row): row is StorefrontProduct => Boolean(row));
}

// Prices the lines on the server, refusing any line its product can no longer fill.
function priceLines(lines: PendingLine[], found: StorefrontProduct[]): Promise<MerchandiseQuote> {
  const lineItems = lines.map((row) => {
    const item = found.find((product) => product.slug === row.slug);
    if (!item || !orderableVariant(item, row)) throw new Error(UNAVAILABLE);
    return { productId: item.id, variantId: row.variantId, quantity: row.quantity };
  });
  return quoteMerchandise(lineItems);
}

// Holds this visit's cart under one storage key, re-reading its products and price whenever its lines change. The cart
// starts empty, as a page drawn on the server shows it, and takes the visit's stored lines before the first paint; it
// stores nothing until then, so the empty start can never write over the cart the shopper already has. A visit with
// nothing stored has nothing to protect: it starts restored and its restore changes nothing, so the page is not redrawn.
// That first answer is never drawn, which is why reading storage for it cannot make the browser's page differ from the server's.
export function useCartLines(key: string): CartLines {
  const [lines, setLineState] = useState<PendingLine[]>([]);
  const [restored, setRestored] = useState(() => typeof window !== 'undefined' && restore(key).length === 0);
  useIsoLayoutEffect(() => {
    const stored = restore(key);
    setLineState((current) => (stored.length || current.length ? stored : current));
    setRestored(true);
  }, [key]);
  const [products, setProducts] = useState<StorefrontProduct[]>([]);
  const [quote, setQuote] = useState<MerchandiseQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const held = useRef(products);
  held.current = products;
  const fresh = useRef(false);
  useEffect(() => {
    if (!restored) return;
    persist(key, lines);
    if (!lines.length) { setLoading(false); setQuote(null); setProducts((current) => (current.length ? [] : current)); return; }
    let cancelled = false;
    const known = fresh.current ? [] : held.current;
    fresh.current = false;
    setLoading(true); setError('');
    loadProducts(lines, known).then(async (found) => {
      if (cancelled) return;
      setProducts(found);
      const priced = await priceLines(lines, found);
      if (!cancelled) setQuote(priced);
    }).catch((failure: Error) => { if (!cancelled) { setQuote(null); setError(failure.message || LOAD_FAILED); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lines, key, refresh, restored]);
  return {
    lines, products, quote, loading, error, fail: setError,
    setLines: (next, known = []) => { if (known.length) setProducts((current) => withKnown(current, known)); setLineState(next); },
    retry: () => { fresh.current = true; setRefresh((value) => value + 1); }
  };
}
